import axios from 'axios';
import { useSyncExternalStore } from 'react';
import i18n from '../i18n';
import {
    getFallbackAvailableLanguage,
    isLanguageEnabled,
    normalizeLocale,
    setEnabledLanguageCodes,
} from '../i18n/languages';
import { getApiBaseUrl } from '../apiBase';

/**
 * Какие языки и гражданства администратор платформы открыл для этого региона (/api/config/regional).
 * Последний ответ кэшируется в localStorage, чтобы при следующем заходе не было «мигания» полного списка.
 */
const CACHE_KEY = 'sp_regional_config';

let state = { languages: null, citizenships: null, loaded: false, failed: false };
const listeners = new Set();

function readCache() {
    try {
        const parsed = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        if (parsed && Array.isArray(parsed.languages) && Array.isArray(parsed.citizenships)) return parsed;
    } catch { /* кэш необязателен */ }
    return null;
}

function writeCache(data) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch { /* кэш необязателен */ }
}

function apply(data, loaded) {
    setEnabledLanguageCodes(data.languages);
    state = { languages: data.languages, citizenships: data.citizenships, loaded, failed: false };
    listeners.forEach((fn) => fn());
}

/** Если выбранный пользователем язык отключили — тихо переключаем на доступный. */
function enforceEnabledLanguage() {
    const current = normalizeLocale(i18n.resolvedLanguage || i18n.language);
    if (isLanguageEnabled(current)) return;
    void i18n.changeLanguage(getFallbackAvailableLanguage());
}

const cached = readCache();
if (cached) apply(cached, false);

let inflight = null;

export function loadRegionalConfig() {
    if (inflight) return inflight;
    inflight = axios
        .get(`${getApiBaseUrl()}/config/regional`, { timeout: 6000 })
        .then(({ data }) => {
            if (!Array.isArray(data?.languages) || !Array.isArray(data?.citizenships)) return;
            const next = { languages: data.languages, citizenships: data.citizenships };
            writeCache(next);
            apply(next, true);
            enforceEnabledLanguage();
        })
        .catch(() => {
            // Сервер недоступен — работаем с кэшем (или без ограничений); регистрацию всё равно проверит бэкенд.
            if (!state.citizenships) {
                state = { ...state, failed: true };
                listeners.forEach((fn) => fn());
            }
        })
        .finally(() => {
            inflight = null;
        });
    return inflight;
}

function subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

/** Состояние конфигурации; компонент перерисуется, когда оно загрузится или изменится. */
export function useRegionalConfig() {
    return useSyncExternalStore(subscribe, () => state);
}

/** Название страны на языке интерфейса (браузерные названия регионов), с запасным вариантом en/ru из справочника. */
export function localizedCountryName(country, language) {
    const lang = normalizeLocale(language);
    if (lang === 'ru') return country.nameRu;
    if (lang === 'en') return country.nameEn;
    try {
        const name = new Intl.DisplayNames([lang], { type: 'region' }).of(country.code);
        if (name && name !== country.code) return name;
    } catch { /* старый браузер */ }
    return country.nameEn;
}
